import React, { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import '../../styles/tiptap.css';
import type { RichTextEditorProps } from './types';
import { getEditorExtensions } from './editorConfig';
import { Toolbar } from './Toolbar';
import { useImageUpload } from './useImageUpload';
import { useEditorActions } from './useEditorActions';
import { MediaLibraryModal } from '../MediaLibraryModal';
import type { MediaItem } from '../../api/modules/upload';

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  placeholder = 'Start typing...',
  minHeight = '200px',
  onImageUpload,
  siteId,
}) => {
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const editor = useEditor({
    extensions: getEditorExtensions(placeholder),
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'focus:outline-none',
      },
    },
  });

  const { imageInputRef, handleImageInputChange, triggerImageUpload } = useImageUpload(
    editor,
    onImageUpload
  );

  const { addLink, addYoutubeVideo, addImageFromUrl } = useEditorActions(editor);

  // Handle selecting image from media library
  const handleLibrarySelect = (media: MediaItem) => {
    setShowMediaLibrary(false);
    if (editor) {
      const alt = window.prompt('Enter alt text for this image (optional):') || '';
      editor
        .chain()
        .focus()
        .setImage({
          src: media.cloudfrontUrl || media.url,
          alt,
        })
        .run();
    }
  };

  // Update editor content when the content prop changes
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className="border border-gray-300 rounded-lg">
      {/* Hidden file input for image upload */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageInputChange}
        className="hidden"
      />

      {/* Toolbar */}
      <Toolbar
        editor={editor}
        onAddLink={addLink}
        onAddYoutube={addYoutubeVideo}
        onAddImageFromUrl={addImageFromUrl}
        onImageUploadClick={triggerImageUpload}
        onSelectFromLibrary={siteId ? () => setShowMediaLibrary(true) : undefined}
      />

      {/* Editor Content */}
      <div
        className="bg-white px-4 py-3 cursor-text tiptap-content rounded-b-lg"
        style={{ minHeight }}
        onClick={() => editor.commands.focus()}
      >
        <EditorContent editor={editor} />
      </div>

      {/* Media Library Modal for image selection */}
      {siteId && (
        <MediaLibraryModal
          isOpen={showMediaLibrary}
          onClose={() => setShowMediaLibrary(false)}
          siteId={siteId}
          selectionMode={true}
          mediaTypeFilter="image"
          onSelect={handleLibrarySelect}
        />
      )}
    </div>
  );
};

export default RichTextEditor;
